import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Container,
    Row,
    Col,
} from '@zillow/zrs-comet';

import {
    Select,
    Label,
    LoadingMask,
    ToggleButtonGroup,
    ToggleButton,
    FormField,
    FormHelp,
    Button,
    Input,
    Spacer,
    Alert,
} from '@zillow/constellation';

import HeadWrapper from 'app/shared/components/HeadWrapper';
import AuthCheckWrapper from 'app/shared/containers/AuthCheckWrapper';
import AnalyticsActions from 'app/shared/actions/AnalyticsActions';
import { AddressSelectors } from 'app/shared/selectors';
import AddressActions from 'app/shared/actions/AddressActions';
import { StyledAutoComplete }
    from 'app/shared/components/Modal/AddPropertyModal/components/AddPropertyForm/StyledComponents';
import LoadingConstants from 'app/shared/constants/LoadingConstants';

const DEFAULT_PAGE_TITLE = 'Rent Comps';

const MarketPage = () => {
    const DEFAULT_PROPERTY_VALUE = 'defaultProperty';
    const DEFAULT_UNIT_VALUE = 'defaultUnit';
    const TOGGLE_OPTIONS = {
        SELECT_PROPERTY: 'SELECT_PROPERTY',
        ENTER_ADDRESS: 'ENTER_ADDRESS',
    };
    const MIN_LENGTH_REQUIRED_BY_API = 7;
    const initialAddressObject = {
        street: '',
        city: '',
        state: '',
        zip: '',
        unit: '',
    };

    const [isLoadingProperties, setIsLoadingProperties] = useState(true);
    const [selectedListingAlias, setSelectedListingAlias] = useState(DEFAULT_PROPERTY_VALUE);
    const [isMultifamilySelected, setIsMultifamilySelected] = useState(false);
    const [loadingUnitsState, setLoadingUnitsState] = useState(LoadingConstants.INITIAL);
    const [hasUnits, setHasUnits] = useState(false);
    const [selectedUnitName, setSelectedUnitName] = useState(DEFAULT_UNIT_VALUE);
    const [selectedToggle, setSelectedToggle] = useState(TOGGLE_OPTIONS.SELECT_PROPERTY);
    const [addressInput, setAddressInput] = useState('');
    const [addressInputError, setAddressInputError] = useState('');
    const [unitInput, setUnitInput] = useState('');
    const [unitInputError, setUnitInputError] = useState('');
    const [chosenAddressObject, setChosenAddressObject] = useState(initialAddressObject);
    const [isAddressChosen, setIsAddressChosen] = useState(false);

    const marketProperties = useSelector(state => state.analytics.marketPropertiesSelect.properties);
    const propertiesByAlias = useSelector(state => state.analytics.marketPropertiesSelect.propertiesByAlias);
    const unitsByFloorplanName = useSelector(state => state.analytics.marketPropertiesSelect.unitsByFloorplanName);
    const addressList = useSelector(state => AddressSelectors.makeAddressList(state));
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(AnalyticsActions.getMarketPropertiesSelect())
            .then(() => setIsLoadingProperties(false))
            .catch(() => setIsLoadingProperties(false));
    }, []);

    useEffect(() => {
        if (loadingUnitsState === LoadingConstants.SUCCESS && Object.keys(unitsByFloorplanName).length === 0) {
            setIsAddressChosen(true);
            setHasUnits(false);
        }
    }, [loadingUnitsState]);

    const getPageCopy = 'The Rent Zestimate® uses millions of data points - plus local market trends - '
        + 'to give you a starting price point';

    const getNoUnitsAlert = 'There are no units for this property. We\'ll juse use the address of the building';

    const getPropertySelectName = (property) => {
        if (!property.name) {
            return property.address;
        }
        if (property.isMultifamily) {
            return property.name;
        }
        return property.address;
    }

    const verifyAddress = async () => {
        try {
            const verifiedAddress = await dispatch(AddressActions.verify({ address: addressInput, unit: unitInput }));
            // manually construct the normalized address since the response will include the unit in its normalized one
            const normalizedAddress = `${verifiedAddress.street}, ${verifiedAddress.city}, `
                + `${verifiedAddress.state} ${verifiedAddress.zip}`;
            setAddressInput(normalizedAddress);
            setAddressInputError('');

            if (unitInput !== '' && verifiedAddress.isValidatedWithoutUnit) {
                setUnitInputError('Unable to validate the property address with this unit number.');
            } else {
                setUnitInputError('');
                setChosenAddressObject({
                    street: verifiedAddress.street,
                    city: verifiedAddress.city,
                    state: verifiedAddress.state,
                    zip: verifiedAddress.zip,
                    unit: unitInput,
                });
                setIsAddressChosen(true);
            }
        } catch ({ message }) {
            setAddressInputError(message);
            setChosenAddressObject(initialAddressObject);
            setIsAddressChosen(false);
        }
    };

    const handleToggleChange = (value) => {
        setSelectedToggle(value);
    };

    const handlePropertySelect = (event) => {
        const listingAlias = event.target.value;
        setSelectedListingAlias(listingAlias);
        setSelectedUnitName(DEFAULT_UNIT_VALUE);
        const selectedProperty = propertiesByAlias[listingAlias];
        setChosenAddressObject({
            street: selectedProperty.street,
            city: selectedProperty.city,
            state: selectedProperty.state,
            zip: selectedProperty.zip,
        });

        if (selectedProperty.isMultifamily) {
            setHasUnits(true);
            setIsAddressChosen(false);
            setIsMultifamilySelected(true);
            setLoadingUnitsState(LoadingConstants.LOADING);

            dispatch(AnalyticsActions.getMarketUnitsForProperty(listingAlias))
                .then(() => setLoadingUnitsState(LoadingConstants.SUCCESS))
                .catch(() => setLoadingUnitsState(LoadingConstants.ERROR));
        } else {
            setIsMultifamilySelected(false);
            setIsAddressChosen(true);
        }
    };

    const handleUnitSelect = (event) => {
        const unitName = event.target.value;
        setSelectedUnitName(unitName);
        setChosenAddressObject({ ...chosenAddressObject, unit: unitName });
        setIsAddressChosen(true);
    };

    const handleSelectAddress = (newAddress) => {
        setAddressInput(newAddress.name);
    };

    const handleAddressChange = (value) => {
        setIsAddressChosen(false);
        setAddressInput(value);
        if (value.length < MIN_LENGTH_REQUIRED_BY_API) {
            return;
        }
        dispatch(AddressActions.autocomplete(value));
    };

    const handleUnitChange = (event) => {
        setUnitInput(event.target.value);
    };

    const handleUseAddress = async () => {
        await verifyAddress();
    };

    const renderChosenAddress = () => {
        if (!isAddressChosen) {
            return null;
        }
        const chosenAddress = 'Selected Address: '
            + `${chosenAddressObject.street} - ${chosenAddressObject.unit}, ${chosenAddressObject.city}, `
            + `${chosenAddressObject.state} ${chosenAddressObject.zip}`;
        return (
            <>
                <hr />
                <Container marginBottom="lg">
                    {chosenAddress}
                </Container>
            </>
        );
    };

    const renderPropertySelection = () => (
        <>
            <Row>
                <Col md={12} lg={6} xl={5}>
                    <LoadingMask loading={isLoadingProperties} style={{display: "block"}}>
                        <Label>Property</Label>
                        <Select
                            name="propertySelect"
                            value={selectedListingAlias}
                            disabled={isLoadingProperties}
                            onChange={handlePropertySelect}
                        >
                            <option disabled={true} value={DEFAULT_PROPERTY_VALUE}>
                                Choose a property
                            </option>
                            {marketProperties.map(property => (
                                <option key={property.listingAlias} value={property.listingAlias}>
                                    {getPropertySelectName(property)}
                                </option>
                            ))}
                        </Select>
                    </LoadingMask>
                </Col>
                {isMultifamilySelected && (
                    <Col md={12} lg={6} xl={5}>
                        {hasUnits ? (
                                <LoadingMask
                                    loading={loadingUnitsState === LoadingConstants.LOADING}
                                    style={{display: "block"}}
                                >
                                    <Label>Unit</Label>
                                    <Select
                                        name="unitSelect"
                                        value={selectedUnitName}
                                        disabled={loadingUnitsState === LoadingConstants.LOADING}
                                        onChange={handleUnitSelect}
                                    >
                                        <option disabled={true} value={DEFAULT_UNIT_VALUE}>
                                            Choose a unit
                                        </option>
                                        {Object.entries(unitsByFloorplanName)
                                            .map(([floorPlanName, units]) => (
                                                <optgroup key={floorPlanName} label={floorPlanName}>
                                                    {units.map(unit => (
                                                        <option
                                                            key={unit.unitId}
                                                            value={unit.unitName}
                                                        >
                                                            {unit.unitName}
                                                        </option>
                                                    ))}
                                                </optgroup>
                                            ))}
                                    </Select>
                                </LoadingMask>
                        ) : (
                            <Spacer marginTop="md">
                                <Alert body={getNoUnitsAlert} />
                            </Spacer>
                        )}
                    </Col>
                )}
            </Row>
        </>
    );

    const renderAddressInput = () => (
        <>
            <Spacer marginBottom="sm">
                <Row>
                    <Col md={12} lg={6} xl={5}>
                        <FormField error={!!addressInputError}>
                            <Label>Property address</Label>
                            <StyledAutoComplete
                                items={addressList}
                                placeholder="Enter an Address"
                                onChange={handleAddressChange}
                                onSelect={handleSelectAddress}
                                value={addressInput}
                            />
                            <FormHelp error={!!addressInputError}>
                                {addressInputError}
                            </FormHelp>
                        </FormField>
                    </Col>
                    <Col md={12} lg={6} xl={5}>
                        <FormField error={!!unitInputError}>
                            <Label>Unit number</Label>
                            <Input
                                data-test="unit-number"
                                aria-label="Unit number"
                                placeholder="Enter a unit number"
                                onChange={handleUnitChange}
                                value={unitInput}
                            />
                            <FormHelp error={!!unitInputError}>
                                {unitInputError}
                            </FormHelp>
                        </FormField>
                    </Col>
                </Row>
            </Spacer>
            <Button buttonType="primary" onClick={handleUseAddress}>
                Use Address
            </Button>
        </>
    );

    return (
        <AuthCheckWrapper requireStrongAuthentication={false}>
            <div>
                <HeadWrapper title={DEFAULT_PAGE_TITLE} />
                <Container
                    marginTop="lg"
                    marginBottom="lg"
                >
                    <Spacer marginBottom="md">
                        <Row>
                            <Col md={12} lg={6} xl={5}>
                                <h2>Stay on top of market trends</h2>
                                <p>{getPageCopy}</p>
                            </Col>
                        </Row>
                    </Spacer>
                    <Spacer marginBottom="md">
                        <Row>
                            <Col md={12} lg={6} xl={5}>
                                <h3>Let's find your property first:</h3>
                                <ToggleButtonGroup
                                    value={selectedToggle}
                                    onChange={handleToggleChange}
                                    aria-label="Toggle address option"
                                >
                                    <ToggleButton value={TOGGLE_OPTIONS.SELECT_PROPERTY}>
                                        Select property
                                    </ToggleButton>
                                    <ToggleButton value={TOGGLE_OPTIONS.ENTER_ADDRESS}>
                                        Enter an address
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Col>
                        </Row>
                    </Spacer>
                    {selectedToggle === TOGGLE_OPTIONS.SELECT_PROPERTY ? (
                        renderPropertySelection()
                    ) : (
                        renderAddressInput()
                    )}
                </Container>
                {renderChosenAddress()}
            </div>
        </AuthCheckWrapper>
    );
};


export default MarketPage;
